import {
  DiscountApplicationMethodEnum,
  IQuoteItemRespSchema,
} from '@monetize/types/app';
import {
  formatCurrency,
  roundNumberToDecimal,
  sortByProductPosition,
} from '@monetize/utils/core';
import { memo, ReactNode } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { MGrid, MGridItem, MText } from '../../../../../components/Monetize';

interface QuoteOfferingCustomDiscountPreviewProps {
  quoteItems: IQuoteItemRespSchema[];
  discountPercentage: number;
  currency: string;
  editMode: boolean;
  hasOfferingLevelDiscount: boolean;
  templateColumns: string;
  columnGap: number;
  discountApplicationMethod?: DiscountApplicationMethodEnum | null;
}

interface QuoteItemRowProps {
  item: IQuoteItemRespSchema;
  discountPercentage: number;
  currency: string;
  editMode: boolean;
  isSequential: boolean;
}

const GridHeaderText = ({ children }: { children: ReactNode }) => (
  <MText fontSize="sm" fontWeight="bold">
    {children}
  </MText>
);

const QuoteItemRow = memo(
  ({
    item,
    discountPercentage,
    currency,
    editMode,
    isSequential,
  }: QuoteItemRowProps) => {
    const customDiscountValue = roundNumberToDecimal(
      editMode ? discountPercentage : (item.customDiscountAmountOrPercent ?? 0),
      10,
    );

    const customDiscountDisplay = `${customDiscountValue}%`;

    const catalogDiscountValue = item.discounts.reduce((acc, discount) => {
      return acc + (discount.discountAmountOrPercent ?? 0);
    }, 0);

    const totalDiscountValue = roundNumberToDecimal(
      isSequential
        ? (1 -
            (1 - catalogDiscountValue / 100) *
              (1 - customDiscountValue / 100)) *
            100
        : Math.min(customDiscountValue + catalogDiscountValue, 100),
      10,
    );

    const discountValueToUse =
      catalogDiscountValue > 0 ? totalDiscountValue : customDiscountValue;

    const beforeDiscountAmount = item.amountWithoutDiscount || 0;
    const afterDiscountAmount = editMode
      ? beforeDiscountAmount * (1 - discountValueToUse / 100)
      : item.amount;

    return (
      <Fragment key={item.id}>
        <MGridItem>
          <MText fontSize="sm">{item.productName || 'N/A'}</MText>
        </MGridItem>
        <MGridItem>
          <MText fontSize="sm">
            {formatCurrency(beforeDiscountAmount, {
              currency,
              maximumFractionDigits: 2,
            })}
          </MText>
        </MGridItem>
        {catalogDiscountValue > 0 ? (
          <>
            <MGridItem>
              <MText fontSize="sm" fontWeight={500}>
                {customDiscountDisplay}
              </MText>
            </MGridItem>
            <MGridItem>
              <MText fontSize="sm" fontWeight={500}>
                {totalDiscountValue}%
              </MText>
            </MGridItem>
          </>
        ) : (
          <MGridItem>
            <MText fontSize="sm" fontWeight={500}>
              {customDiscountDisplay}
            </MText>
          </MGridItem>
        )}
        <MGridItem>
          <MText fontSize="sm" color="tPurple.safety">
            {formatCurrency(afterDiscountAmount, {
              currency,
              maximumFractionDigits: 2,
            })}
            {' *'}
          </MText>
        </MGridItem>
      </Fragment>
    );
  },
);

export const QuoteOfferingCustomDiscountPreview = ({
  quoteItems,
  discountPercentage,
  currency,
  editMode,
  hasOfferingLevelDiscount,
  templateColumns,
  columnGap,
  discountApplicationMethod,
}: QuoteOfferingCustomDiscountPreviewProps) => {
  const isSequential =
    discountApplicationMethod === DiscountApplicationMethodEnum.SEQUENTIAL;
  const sortedQuoteItems = sortByProductPosition(quoteItems);

  return (
    <MGrid
      templateColumns={templateColumns}
      gap={columnGap}
      alignItems="center"
      mb={4}
    >
      <MGridItem>
        <GridHeaderText>Product Name</GridHeaderText>
      </MGridItem>
      <MGridItem>
        <GridHeaderText>Amount Before Discount</GridHeaderText>
      </MGridItem>
      {hasOfferingLevelDiscount ? (
        <>
          <MGridItem>
            <GridHeaderText>Custom Discount</GridHeaderText>
          </MGridItem>
          <MGridItem>
            <GridHeaderText>Total Discount</GridHeaderText>
          </MGridItem>
        </>
      ) : (
        <MGridItem>
          <GridHeaderText>Discount</GridHeaderText>
        </MGridItem>
      )}
      <MGridItem>
        <GridHeaderText>Amount After Discount</GridHeaderText>
      </MGridItem>

      {sortedQuoteItems.map((item) => (
        <QuoteItemRow
          key={item.id}
          item={item}
          discountPercentage={discountPercentage}
          currency={currency}
          editMode={editMode}
          isSequential={isSequential}
        />
      ))}
    </MGrid>
  );
};
